import type { BucketedItemBalance } from '@api/vendor-portal';
import type { Nudge } from '@components/ui/NudgeStrip';

/**
 * How long something may sit before it is worth saying out loud.
 *
 * QC first, because material waiting on a verdict is material a run cannot start on — three
 * days is roughly when a site notices it is short. Blocked stock is slower: it is not holding
 * anything up, it is just accumulating, and a week is when "we will deal with it" has become
 * "nobody is going to". Both are deliberately generous; a nudge that fires on day one is noise.
 */
export const QC_NUDGE_DAYS = 3;
export const BLOCKED_NUDGE_DAYS = 7;

/**
 * How much warning is useful before a batch goes off.
 *
 * Thirty days, because that is roughly the window in which something can still be DONE about
 * it — run it, ship it, or tell the brand it is going to be written off. A warning that arrives
 * on the day it expires is not a warning, it is a report.
 */
export const EXPIRY_NUDGE_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Days remaining. Negative once it has gone off, which the caller reads as already blocked. */
export function daysUntil(value: string | null, now: number = Date.now()): number | null {
    if (!value) return null;
    const then = new Date(value).getTime();
    if (!Number.isFinite(then)) return null;
    return Math.floor((then - now) / DAY_MS);
}

export function daysSince(value: string | null, now: number = Date.now()): number | null {
    if (!value) return null;
    const then = new Date(value).getTime();
    if (!Number.isFinite(then)) return null;
    return Math.floor((now - then) / DAY_MS);
}

/**
 * What is sitting unattended, across whatever the caller is holding.
 *
 * PER BATCH, NOT PER ITEM, because the unit of the decision is a batch: one item can hold three
 * lots, two cleared this morning and one stuck since last month, and an item-level count would
 * hide exactly the one worth chasing.
 *
 * COUNTED WHERE THE CALLER IS LOOKING. The stock screen passes the view somebody is on; the
 * home screen passes materials and finished goods together, because a person opening the portal
 * wants everything that needs them, not everything of one kind.
 *
 * These are QUERIES OVER WHAT IS ON SCREEN, not configured alerts. Nothing here has to be set
 * up, nothing fires by email, and a site that fixes the thing sees the nudge go away on the next
 * read. Alerts a brand configures are a different feature and a later one.
 */
export function buildStockNudges(
    items: BucketedItemBalance[],
    now: number = Date.now(),
): Nudge[] {
    const staleQc: number[] = [];
    const staleBlocked: number[] = [];
    // Days remaining, and how much stock is riding on them.
    const expiring: Array<{ days: number; quantity: number }> = [];

    for (const item of items) {
        for (const batch of item.batches) {
            // EXPIRY IS ABOUT STOCK, NOT AGE, so it is judged separately and only on batches
            // that still hold something. A batch that has gone off with nothing in it is a row
            // in a table, not a problem.
            const daysLeft = daysUntil(batch.expiryDate, now);
            if (
                daysLeft !== null &&
                daysLeft >= 0 &&
                daysLeft <= EXPIRY_NUDGE_DAYS &&
                batch.quantity > 0 &&
                batch.availability !== 'BLOCKED'
            ) {
                expiring.push({ days: daysLeft, quantity: batch.quantity });
            }

            const age = daysSince(batch.receivedAt, now);
            if (age === null) continue;
            // EMPTY BATCHES DO NOT NAG, the same rule the expiry branch above applies and
            // for the same reason. Unruled stock may be dispatched, so a batch can leave before
            // anyone rules on it and then sit at zero in IN_QC forever — ageing, unresolvable
            // by the site, and firing this every day. Nobody can QC goods that have gone.
            if (batch.availability === 'IN_QC' && batch.quantity > 0 && age >= QC_NUDGE_DAYS) {
                staleQc.push(age);
            }
            else if (batch.availability === 'BLOCKED' && age >= BLOCKED_NUDGE_DAYS) {
                staleBlocked.push(age);
            }
        }
    }

    const nudges: Nudge[] = [];

    if (staleQc.length > 0) {
        nudges.push({
            id: 'qc',
            tone: 'warning',
            title: `${staleQc.length} batch${staleQc.length === 1 ? '' : 'es'} waiting on QC`,
            detail: `Oldest ${Math.max(...staleQc)} days. Nothing can be run from them until they pass.`,
        });
    }

    if (expiring.length > 0) {
        const soonest = Math.min(...expiring.map((entry) => entry.days));
        const quantity = expiring.reduce((sum, entry) => sum + entry.quantity, 0);

        nudges.push({
            id: 'expiring',
            tone: 'warning',
            title: `${quantity} expiring within ${EXPIRY_NUDGE_DAYS} days`,
            detail:
                soonest === 0
                    ? 'The soonest goes off today. After that it can only be written off.'
                    : `The soonest has ${soonest} day${soonest === 1 ? '' : 's'} left. Run it, ship it, or it becomes a write-off.`,
        });
    }

    if (staleBlocked.length > 0) {
        nudges.push({
            id: 'blocked',
            tone: 'critical',
            title: `${staleBlocked.length} blocked batch${staleBlocked.length === 1 ? '' : 'es'} still here`,
            detail: `Oldest ${Math.max(...staleBlocked)} days. Release it, send it back, or write it off.`,
        });
    }

    return nudges;
}

/**
 * Orders nobody at the site has said they have seen.
 *
 * The one nudge that is not about stock, and the one the brand cares most about: an
 * unacknowledged order is a run that may not have started, and the brand has no other way to
 * tell that apart from a run that is going fine.
 */
export function buildOrderNudges(
    orders: Array<{ needsAcknowledgement: boolean }>,
): Nudge[] {
    const unseen = orders.filter((order) => order.needsAcknowledgement).length;
    if (unseen === 0) return [];

    return [
        {
            id: 'unacknowledged',
            tone: 'warning',
            title: `${unseen} order${unseen === 1 ? '' : 's'} not yet acknowledged`,
            detail:
                unseen === 1
                    ? 'The brand cannot tell whether this one has been started.'
                    : 'The brand cannot tell whether these have been started.',
        },
    ];
}
